/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { ActionOutputMappingItem, defaultValue } from './ActionOutputMapping';
import { ActionInvokeDataSourceItem } from '../ActionInvokeDataSource/ActionInvokeDataSource';
import { OutAttrLevelType } from 'componentregistry/registry/constants';

const ActionOutputMappingManager: ActionManagerFn<'ActionOutputMapping'> = (
  platform: Platform,
): ActionManager<'ActionOutputMapping'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionOutputMapping'),
    defaultValue,
    props: {
      ...baseActionProps,
      attribute: {
        label: 'Attribute',
        type: 'InvokeAttributeChooser',
        required: true,
        aliasType: 'STORE',
        getAttributeDataTypes: () => 'ALL',
        getAlias: (page, item, { pageId }) => {
          return {
            alias: (page[item.parent] as ActionInvokeDataSourceItem).props
              .dataSource,
            embeddedPageId: pageId,
          };
        },
        preventMultiEdit: true,
      },
      dataLevel: {
        label: 'Level',
        type: 'select',
        options: OutAttrLevelType,
        required: true,
      },
      targetAlias: {
        label: 'Target Datastore Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
          'ReadOnlyDataStore',
        ],
      },
      targetAttribute: {
        label: 'Target Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
        getAlias: (page, item, { pageId }) => {
          return {
            alias: (page[item.itemId] as ActionOutputMappingItem).props
              .targetAlias,
            embeddedPageId: pageId,
          };
        },
        attributeRefreshCallback(cb) {
          callback = cb;
        },
      },
      targetVariable: {
        label: 'Target Variable',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'attribute') {
        return {
          [property]: value,
          targetVariable: defaultValue.targetVariable,
        };
      }
      if (property === 'targetAlias') {
        callback();
        return {
          [property]: value,
          targetAttribute: defaultValue.targetAttribute,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'targetVariable':
          return !!props.attribute && !(props.dataLevel === 'dataSource');
        case 'targetAlias':
          return !!props.attribute && props.dataLevel === 'dataSource';
        case 'targetAttribute':
          return !!props.targetAlias && props.dataLevel === 'dataSource';
        default:
          return true;
      }
    },
    name: 'Output Mapping',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return parent.type === 'ActionInvokeDataSource';
    },
    getDisplayText: (item) => item.props.attribute,
    icon: { iconName: 'value-absolute' },
    description: `**Overview**<br/>
The Action Output Mapping component functions as a response handler within an ActionInvokeDataSource context. It extracts specific data attributes from the data source's execution result and routes them to a destination, which can be either a variable or a specific field in a different Data Store.<br/><br/>
This component employs dynamic visibility logic to toggle between two distinct modes based on the dataLevel property: mapping to a target variable or mapping to a target data store entity. It ensures data consistency by enforcing parent-child relationships and implementing automatic reset triggers when dependencies change.

**Key Features**<br/>
* **Dual Mapping Modes:** Supports two distinct routing paths controlled by the dataLevel property:
  * *Variable Mapping:* Directs output to a specific variable expression (active when dataLevel is not 'dataSource').
  * *Data Store Mapping:* Directs output to a specific attribute within a target Data Store (active when dataLevel is 'dataSource').
* **Conditional Visibility:** Implements isPropertyVisible logic to dynamically show or hide the targetAlias/targetAttribute fields versus the targetVariable field, depending on the selected dataLevel.
* **Context-Aware Source Selection:** The source attribute property automatically derives its context from the parent ActionInvokeDataSource's configuration (props.dataSource), ensuring users can only map fields available in the current execution context.
* **Cascading Reset Logic:** Automatically clears dependent properties to prevent stale configuration:
  * Changing attribute resets targetVariable.
  * Changing targetAlias resets targetAttribute.

**Properties**<br/>
* **Attribute (Required):** The source field to extract from the invoked data source.
  * *Type:* InvokeAttributeChooser with aliasType: 'STORE'.
* **Level (Required):** Defines the destination scope (referenced as dataLevel).
  * *Logic:* Determines whether the UI displays Data Store selection fields or Variable expression fields.
* **Target Datastore Alias:** The destination Data Store entity.
  * *Visibility:* Only visible when Level is set to 'dataSource'.
  * *Supported Types:* CloudDataStore, DataStore, LocalDataStore, TransientDataStore, ReadOnlyDataStore.
* **Target Attribute:** The specific field within the Target Datastore Alias where data will be saved.
  * *Visibility:* Only visible when targetAlias is selected and Level is 'dataSource'.
* **Target Variable:** An expression defining the destination variable.
  * *Visibility:* Only visible when Level is not 'dataSource'.
  * *Type:* Expression returning a string.

**Use Cases**<br/>
* **Response Extraction:** Capturing a specific value from an API response (e.g., an ID or status) and storing it in a local variable for use in subsequent actions.
* **Data Chaining:** Directly transferring data from the result of one Data Source execution into the storage of another Data Store (e.g., saving a result from a transient store to a CloudDataStore).`,
  };
};

export default ActionOutputMappingManager;
